import { forwardRef } from 'react';

import { mergeSlotProps } from '@mui/material/utils';

import { Menu, type MenuProps } from '../menu';

import type { DropdownMenuRenderTriggerButtonParams } from './use-dropdown-menu';
import { useDropdownMenu } from './use-dropdown-menu';

export type DropdownMenuProps = React.PropsWithChildren<{
  /**
   * A selected group of props from MenuProps
   */
  menuProps?: Omit<
    MenuProps,
    | 'anchorEl'
    | 'id'
    | 'open'
    | 'onClose'
    | 'onClick'
    | 'children'
    | 'MenuListProps'
    | 'slots'
    | 'TransitionProps'
  >;
  /**
   * Render the trigger button.
   *
   * **Note:** The `renderTriggerButton` prop must return a `Button` or `IconButton` component or a compatible custom component
   * that correctly forwards the params. This ensures proper integration
   * with the DropdownMenu internal logic as well as maintaining good accessibility.
   *
   * @param {object} params
   * @returns {ReactNode}
   */
  renderTriggerButton: (
    params: DropdownMenuRenderTriggerButtonParams,
  ) => React.ReactNode;
}>;

export const DropdownMenu = forwardRef<HTMLDivElement, DropdownMenuProps>(
  ({ renderTriggerButton, menuProps, children }: DropdownMenuProps, ref) => {
    const {
      dropdownAnchorEl,
      dropdownMenuId,
      dropdownIsOpen,
      dropdownMenuTriggerButtonParams,
      dropdownMenuListParams,
      handleDropdownMenuClose,
    } = useDropdownMenu();

    return (
      <>
        {renderTriggerButton(dropdownMenuTriggerButtonParams)}
        <Menu
          id={dropdownMenuId}
          ref={ref}
          anchorEl={dropdownAnchorEl}
          open={dropdownIsOpen}
          onClose={handleDropdownMenuClose}
          onClick={handleDropdownMenuClose}
          {...menuProps}
          slotProps={{
            ...menuProps?.slotProps,
            list: mergeSlotProps(
              menuProps?.slotProps?.list,
              dropdownMenuListParams,
            ),
          }}
        >
          {children}
        </Menu>
      </>
    );
  },
);
